// =============================================================================
// Semantic Parser for QOps Actividades
// Pure TypeScript — no dependencies, no API calls, no React
// =============================================================================

export interface ParsedActividad {
  textoOriginal: string
  titulo: string
  clienteRef?: string
  contactoRef?: string
  proyectoRef?: string
  ubicacionRef?: string
  tipoActividadCodigo?: string
  etiquetaRefs: string[]
  fechaAgendada?: Date
  horaAgendada?: string
  fechaLimite?: Date
  horaLimite?: string
  prioridad: 'A' | 'AA' | 'AAA' | 'B' | 'C'
  area?: 'TRABAJO' | 'ADMINISTRATIVO' | 'PERSONAL'
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_CODES = [
  'LLAMAR',
  'PROG',
  'IR',
  'REVISAR',
  'REUNIR',
  'INSTALAR',
  'DOCUMENTAR',
  'CAPACITAR',
  'INVESTIGAR',
  'ANALIZAR',
] as const

const AREA_MAP: Record<string, ParsedActividad['area']> = {
  T: 'TRABAJO',
  A: 'ADMINISTRATIVO',
  P: 'PERSONAL',
}

const WEEKDAYS_ES: Record<string, number> = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  miércoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
  sábado: 6,
}

const MONTHS_ES: Record<string, number> = {
  ene: 0,
  feb: 1,
  mar: 2,
  abr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  ago: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dic: 11,
}

// ---------------------------------------------------------------------------
// Date Parsing
// ---------------------------------------------------------------------------

/**
 * Parse a date expression used by FEC: and LIM: semantic codes.
 *
 * Supported formats:
 *   - Relative:       hoy, mañana, manana
 *   - Weekday:        lunes, viernes, miércoles
 *   - Weekday+time:   viernes_1500
 *   - Specific date:  18Mar, 5Ene
 *   - Date+time:      18Mar_1100
 *
 * Returns null if the expression cannot be parsed.
 */
export function parseDateExpression(
  expr: string,
  referenceDate?: Date,
): { date: Date; time?: string } | null {
  const ref = referenceDate ?? new Date()
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
  const trimmed = expr.trim()

  if (!trimmed) return null

  // --- Relative: hoy ---
  if (/^hoy$/i.test(trimmed)) {
    return { date: today }
  }

  // --- Relative: mañana / manana ---
  if (/^ma[ñn]ana$/i.test(trimmed)) {
    const d = new Date(today)
    d.setDate(d.getDate() + 1)
    return { date: d }
  }

  // --- Weekday (with optional _HHMM time) ---
  const weekdayMatch = trimmed.match(
    /^([a-záéíóúñü]+?)(?:_(\d{4}))?$/i,
  )
  if (weekdayMatch) {
    const dayName = weekdayMatch[1].toLowerCase()
    const timePart = weekdayMatch[2]

    if (dayName in WEEKDAYS_ES) {
      const targetDay = WEEKDAYS_ES[dayName]
      const currentDay = today.getDay()
      let diff = targetDay - currentDay
      if (diff <= 0) diff += 7 // always next occurrence
      const d = new Date(today)
      d.setDate(d.getDate() + diff)

      const result: { date: Date; time?: string } = { date: d }
      if (timePart) {
        result.time = formatHHMM(timePart)
      }
      return result
    }
  }

  // --- Specific date: DDMon or DDMon_HHMM ---
  const specificMatch = trimmed.match(
    /^(\d{1,2})([a-záéíóúñü]{3})(?:_(\d{4}))?$/i,
  )
  if (specificMatch) {
    const day = parseInt(specificMatch[1], 10)
    const monthAbbr = specificMatch[2].toLowerCase()
    const timePart = specificMatch[3]

    if (monthAbbr in MONTHS_ES) {
      const month = MONTHS_ES[monthAbbr]
      let year = ref.getFullYear()
      // If the date has already passed this year, use next year
      const candidate = new Date(year, month, day)
      if (candidate < today) {
        year += 1
      }
      const d = new Date(year, month, day)

      const result: { date: Date; time?: string } = { date: d }
      if (timePart) {
        result.time = formatHHMM(timePart)
      }
      return result
    }
  }

  return null
}

/**
 * Convert a 4-digit string "HHMM" into "HH:MM" format.
 */
function formatHHMM(raw: string): string {
  const hh = raw.slice(0, 2)
  const mm = raw.slice(2, 4)
  return `${hh}:${mm}`
}

// ---------------------------------------------------------------------------
// Token-boundary awareness helpers
// ---------------------------------------------------------------------------

/**
 * Build a regex that matches a pattern only at token boundaries.
 * A token boundary is the start of the string or preceded by whitespace.
 * The regex uses a lookbehind for whitespace or anchors to ^ via alternation.
 */
function tokenBoundaryRegex(corePattern: string, flags: string = ''): RegExp {
  return new RegExp(`(?:^|(?<=\\s))${corePattern}`, flags)
}

// ---------------------------------------------------------------------------
// Extraction helpers
// ---------------------------------------------------------------------------

/**
 * Remove a matched substring from the working text.
 * Returns the updated text.
 */
function removeMatch(text: string, match: RegExpMatchArray): string {
  const start = match.index!
  const end = start + match[0].length
  return text.slice(0, start) + text.slice(end)
}

// ---------------------------------------------------------------------------
// Core Parser
// ---------------------------------------------------------------------------

/**
 * Parse a single line of text into a structured ParsedActividad.
 */
export function parseActividad(
  line: string,
  referenceDate?: Date,
): ParsedActividad {
  const result: ParsedActividad = {
    textoOriginal: line,
    titulo: '',
    etiquetaRefs: [],
    prioridad: 'A',
  }

  let text = line

  // 1. CLI:name — client reference
  const cliRe = tokenBoundaryRegex('CLI:(\\S+)')
  const cliMatch = text.match(cliRe)
  if (cliMatch) {
    result.clienteRef = cliMatch[1]
    text = removeMatch(text, cliMatch)
  }

  // 2. Action types — LLAMAR:, PROG:, IR:, etc.
  //    Captures ONLY the code + colon, not what follows.
  const actionPattern = ACTION_CODES.join('|')
  const actionRe = tokenBoundaryRegex(`(${actionPattern}):`, 'i')
  const actionMatch = text.match(actionRe)
  if (actionMatch) {
    result.tipoActividadCodigo = actionMatch[1].toUpperCase()
    text = removeMatch(text, actionMatch)
  }

  // 3. @name — contact reference (single word, supports unicode)
  const atRe = tokenBoundaryRegex('@([\\w\\u00C0-\\u024F]+)')
  const atMatch = text.match(atRe)
  if (atMatch) {
    result.contactoRef = atMatch[1]
    text = removeMatch(text, atMatch)
  }

  // 4. FEC:expr — scheduled date/time
  const fecRe = tokenBoundaryRegex('FEC:(\\S+)')
  const fecMatch = text.match(fecRe)
  if (fecMatch) {
    const parsed = parseDateExpression(fecMatch[1], referenceDate)
    if (parsed) {
      result.fechaAgendada = parsed.date
      result.horaAgendada = parsed.time
    }
    text = removeMatch(text, fecMatch)
  }

  // 5. LIM:expr — deadline date/time
  const limRe = tokenBoundaryRegex('LIM:(\\S+)')
  const limMatch = text.match(limRe)
  if (limMatch) {
    const parsed = parseDateExpression(limMatch[1], referenceDate)
    if (parsed) {
      result.fechaLimite = parsed.date
      result.horaLimite = parsed.time
    }
    text = removeMatch(text, limMatch)
  }

  // 6. +name — project reference
  const projRe = tokenBoundaryRegex('\\+([\\w\\u00C0-\\u024F]+)')
  const projMatch = text.match(projRe)
  if (projMatch) {
    result.proyectoRef = projMatch[1]
    text = removeMatch(text, projMatch)
  }

  // 7. EN:name — location reference
  const enRe = tokenBoundaryRegex('EN:(\\S+)')
  const enMatch = text.match(enRe)
  if (enMatch) {
    result.ubicacionRef = enMatch[1]
    text = removeMatch(text, enMatch)
  }

  // 8. #tag — tags (multiple allowed, unicode/accent support)
  const tagRe = tokenBoundaryRegex('#([\\w\\u00C0-\\u024F]+)', 'g')
  let tagMatch: RegExpExecArray | null
  const tagMatches: RegExpExecArray[] = []
  while ((tagMatch = tagRe.exec(text)) !== null) {
    result.etiquetaRefs.push(tagMatch[1])
    tagMatches.push({ ...tagMatch } as RegExpExecArray)
  }
  // Remove tags from text in reverse order to preserve indices
  for (let i = tagMatches.length - 1; i >= 0; i--) {
    text = removeMatch(text, tagMatches[i])
  }

  // 9. Priority: literal names (AAA, AA, B, C) or symbols (!!!, !!, !)
  //    Must be at token boundary (space or start)
  //    Check literal names first (longer match wins), then symbols
  const prioLiteralRe = tokenBoundaryRegex('(AAA|AA|(?<![A-Za-z])[B](?![A-Za-z])|(?<![A-Za-z])[C](?![A-Za-z]))(?=\\s|$|-)')
  const prioLiteralMatch = text.match(prioLiteralRe)
  if (prioLiteralMatch) {
    const val = prioLiteralMatch[1].toUpperCase()
    if (val === 'AAA' || val === 'AA' || val === 'A' || val === 'B' || val === 'C') {
      result.prioridad = val as ParsedActividad['prioridad']
    }
    text = removeMatch(text, prioLiteralMatch)
  } else {
    const prioTripleRe = tokenBoundaryRegex('!!!(?!!)')
    const prioDoubleRe = tokenBoundaryRegex('!!(?!!)')
    const prioSingleRe = tokenBoundaryRegex('!(?!!)')
    const prioTripleMatch = text.match(prioTripleRe)
    if (prioTripleMatch) {
      result.prioridad = 'AAA'
      text = removeMatch(text, prioTripleMatch)
    } else {
      const prioDoubleMatch = text.match(prioDoubleRe)
      if (prioDoubleMatch) {
        result.prioridad = 'AA'
        text = removeMatch(text, prioDoubleMatch)
      } else {
        const prioSingleMatch = text.match(prioSingleRe)
        if (prioSingleMatch) {
          result.prioridad = 'A'
          text = removeMatch(text, prioSingleMatch)
        }
      }
    }
  }

  // 10. AREA:T/A/P
  const areaRe = tokenBoundaryRegex('AREA:([TAP])', 'i')
  const areaMatch = text.match(areaRe)
  if (areaMatch) {
    const key = areaMatch[1].toUpperCase()
    result.area = AREA_MAP[key]
    text = removeMatch(text, areaMatch)
  }

  // 11. Residue → titulo (clean up whitespace)
  result.titulo = text.replace(/\s{2,}/g, ' ').trim()

  return result
}

// ---------------------------------------------------------------------------
// Bulk Parser
// ---------------------------------------------------------------------------

/**
 * Parse multiline text into an array of ParsedActividad.
 * Splits by newline, filters empty/whitespace-only lines.
 */
export function parseActividadesBulk(
  text: string,
  referenceDate?: Date,
): ParsedActividad[] {
  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line) => parseActividad(line, referenceDate))
}
